import React, { useEffect } from "react";
import { Box, SpaceBetween, Badge } from "@cloudscape-design/components";
import { useAnimatedCounter } from "../../hooks/useAnimatedCounter";
import HorizontalCardContainer from "../common/HorizontalCardContainer";
import Card from "../common/Card";
import { usePnLStore } from "../../store/pnl-store";
import { useNotificationStore } from "../../store/notification-store";
import { getCurrentAndPreviousQuarters } from "../../utils/quarterUtils";
import { 
  ViewName, 
  Scenario, 
  CurrencyType, 
  ChannelType, 
  ReportingDataSourceType,
  PartitionByValues 
} from '@amzn/finspace-service-typescript-client';
import "./revenue-cards.scss";
import {
  A1PTOT_PRODUCT_LINE,
  FINSPACE_CLIENT_ID,
  GMS,
  KEY_METRICS_CONFIG,
  UNITS_SERVED,
  WW_MARKETPLACE_LIST
} from "src/constants";
import { KeyMetricsCardSkeleton } from "./CardSkeletons";

const CHAR_WIDTH_MULTIPLIER = 0.91;

interface CardData {
  id: string;
  title: string;
  currentValue: number;
  currentPeriod: string;
  previousValue: number;
  previousPeriod: string;
}

interface PnLEntry {
  reportingLine?: string;
  records?: RecordEntry[];
}

interface RecordEntry {
  recordPartitions?: Record<string, string>;
  metrics?: Record<string, number>;
}

interface PnLResponse {
  pnl?: PnLEntry[];
}

interface AnimatedMetricCardProps {
  card: CardData;
}

const AnimatedMetricCard: React.FC<AnimatedMetricCardProps> = ({ card }) => {
  const change = card.previousValue === 0 ? 0 : ((card.currentValue - card.previousValue) / card.previousValue) * 100;
  const isPositive = change >= 0;
  const changeFormatted = Math.abs(change).toFixed(2);
  const isUnitsServed = card.title.includes('Units served');
  const currencySymbol = isUnitsServed ? '' : '$';
  
  const { value: animatedValue } = useAnimatedCounter({
    targetValue: card.currentValue,
    decimals: 2
  });

  // Calculate width based on final value character count (with 2 decimals to match animatedValue)
  const finalText = `${currencySymbol}${card.currentValue.toFixed(2)}B/`;
  const charWidth = finalText.length * CHAR_WIDTH_MULTIPLIER;
  
  return (
    <Card key={card.id} width={360} height="auto" className="revenue-card">
      <SpaceBetween size="xs">
        <Box className="revenue-card-header-box">
          <div className="revenue-card-header-content">
            <span className="revenue-card-title">{card.title}</span>
            <Badge color={isPositive ? "green" : "red"}>
              {isPositive ? '+' : '-'}{changeFormatted}%
            </Badge>
          </div>
        </Box>
        
        <Box className="revenue-card-body-box">
          <div className="value-section">
            <div className="value-column">
              <h1 className="main-value" style={{ minWidth: `${charWidth}ch` }}>{currencySymbol}{animatedValue}B/</h1>
              <span className="period">{card.currentPeriod}</span>
            </div>
            <div className="subtext-column">
              <span className="subtext-amount">{currencySymbol}{card.previousValue}B</span>
              <small className="subtext">{card.previousPeriod}</small>
            </div>
          </div>
        </Box>

        <Box className="divider-box">
          <div className="divider"></div>
        </Box>

        <Box>
          <p className="r">
            Quarterly performance is {isPositive ? 'up' : 'dropped'} by {changeFormatted}%
          </p>
        </Box>
      </SpaceBetween>
    </Card>
  );
};

const RevenueCards: React.FC = () => {
  const { getPnL, isLoading, error } = usePnLStore();
  const { error: showErrorNotification } = useNotificationStore();
  const [response, setResponse] = React.useState<PnLResponse | null>(null);

  // Get current and previous quarters dynamically
  const quarters = getCurrentAndPreviousQuarters();
  
  // Build the request
  const request = React.useMemo(() => ({
    traceId: `dashboard-${Date.now()}`,
    clientId: FINSPACE_CLIENT_ID,
    viewName: ViewName.PNL,
    filters: {
      scenario: [Scenario.ACTUALS],
      periods: [quarters.current, quarters.previous],
      currency: [CurrencyType.USD_NOMINAL_AMOUNT],
      marketplace: WW_MARKETPLACE_LIST,
      channelType: [ChannelType.RETAIL, ChannelType.FBA, ChannelType.MFN],
      productId: [A1PTOT_PRODUCT_LINE],
      reportingDataSource: [ReportingDataSourceType.INCLUDING_ADS],
      reportingLines: [GMS, UNITS_SERVED],
    },
    partitionBy: [PartitionByValues.PERIODS],
  }), []);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getPnL(request);
      setResponse(data);
    };
    fetchData();
  }, [getPnL, request]);

  // Show error notification if there's an error
  useEffect(() => {
    if (error) {
      showErrorNotification(
        'Failed to Load Key Metrics'
      );
    }
  }, [error, showErrorNotification]);

  // Transform response data into card data
  const cards: CardData[] = React.useMemo(() => {
    if (!response?.pnl) return [];

    return response.pnl
      .filter((entry: PnLEntry) => entry.reportingLine && KEY_METRICS_CONFIG[entry.reportingLine])
      .map((entry: PnLEntry) => {
        const reportingLine = entry.reportingLine!;
        const config = KEY_METRICS_CONFIG[reportingLine];
        
        // Parse all records and sort by period to get current and previous
        const sortedRecords = (entry.records || [])
          .map((record: any) => ({
            period: record.recordPartitions?.periods || "",
            amount: record.metrics?.amounts || 0,
            record
          }))
          .sort((a: any, b: any) => b.period.localeCompare(a.period)); // Sort descending to get latest first
        
        const currentRecord = sortedRecords[0];
        const previousRecord = sortedRecords[1];
        
        const currentValue = currentRecord?.amount || 0;
        const previousValue = previousRecord?.amount || 0;
        
        const currentPeriod = currentRecord?.period || "Current";
        const previousPeriod = previousRecord?.period || "Previous";
        
        return {
          id: `${entry.reportingLine}Card`,
          title: config.title,
          currentValue,
          currentPeriod,
          previousValue,
          previousPeriod,
        };
      });
  }, [response]);

  // Always render the card structure
  return (
    <HorizontalCardContainer ariaLabel="Revenue metrics cards">
      {isLoading ? (
        Object.keys(KEY_METRICS_CONFIG).map((key) => <KeyMetricsCardSkeleton key={key} />)
       ) : error || cards.length === 0 ? (
        // Show placeholder cards on error or when no data
        Object.values(KEY_METRICS_CONFIG).map((config) => (
          <Card key={`${config.title}-placeholder`} width={360} height="auto" className="revenue-card">
            <Box textAlign="center" padding="xxl" color="text-body-secondary">
              <Box fontSize="heading-m">-</Box>
              <Box margin={{ top: "s" }}>
                {config.title}
              </Box>
            </Box>
          </Card>
        ))
      ) : (
        // Show actual data cards
        cards.map((card) => (
          <AnimatedMetricCard key={card.id} card={card} />
        ))
      )}
    </HorizontalCardContainer>
  );
};

export default RevenueCards;
